/**
 * 25. Supplier Invoices — list; capture form with OCR; three-way match
 * status; approval; payment.
 */
import {
  Card,
  PageHeader,
  DataTable,
  Skeleton,
  StatusBadge,
  Badge,
} from "@/components/ui";
import { useTranslation } from "@/lib/i18n";
import { PendingButton } from "@/components/PendingAction";
import { useApiList } from "@/lib/api/hooks";
import { formatMoney, formatDate } from "@/lib/money";

interface SupplierInvoiceRow {
  id: string;
  invoiceNumber: string;
  invoiceDate: string;
  dueDate: string | null;
  supplierName: string;
  status: "Received" | "Matched" | "Approved" | "Posted" | "Paid";
  matchStatus: "NotMatched" | "Matched" | "ThreeWayMatched" | null;
  total: string;
  currencyCode: string;
}

export function SupplierInvoicesPage() {
  const { t } = useTranslation();
  const { data, isLoading } = useApiList<SupplierInvoiceRow>(
    ["finance", "supplier-invoices"],
    "/finance/supplier-invoices",
  );

  return (
    <div>
      <PageHeader
        title="Supplier Invoices"
        subtitle="Capture with OCR, three-way match, approve, pay."
        actions={
          <PendingButton variant="primary" reason="invoiceEditor" slice="ED-W4">
            {t("documentActions.captureSupplierInvoice")}
          </PendingButton>
        }
      />
      {isLoading || !data ? (
        <Skeleton rows={10} />
      ) : (
        <Card title={`Invoices (${data.total})`}>
          <DataTable<SupplierInvoiceRow>
            rows={data.items}
            columns={[
              {
                key: "invoiceNumber",
                label: "Number",
                render: (r) => (
                  <span className="atlas-mono">{r.invoiceNumber}</span>
                ),
              },
              {
                key: "invoiceDate",
                label: "Date",
                render: (r) => formatDate(r.invoiceDate),
              },
              { key: "supplierName", label: "Supplier" },
              {
                key: "total",
                label: "Total",
                numeric: true,
                render: (r) => formatMoney(r.total, r.currencyCode),
              },
              {
                key: "matchStatus",
                label: "Match",
                render: (r) =>
                  r.matchStatus === "ThreeWayMatched" ? (
                    <Badge tone="success">3-way</Badge>
                  ) : r.matchStatus === "Matched" ? (
                    <Badge tone="primary">2-way</Badge>
                  ) : (
                    <Badge tone="warning">Unmatched</Badge>
                  ),
              },
              {
                key: "status",
                label: "Status",
                render: (r) => <StatusBadge status={r.status} />,
              },
            ]}
          />
        </Card>
      )}
    </div>
  );
}
